import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class HtmlSanitizer {
  // Services
  private readonly document = inject(DOCUMENT);

  // Properties & Signals
  // Whitelist of allowed HTML tags
  private readonly _tagWhitelist: { [key: string]: boolean } = {
    A: true,
    ABBR: true,
    B: true,
    BLOCKQUOTE: true,
    BR: true,
    CENTER: true,
    CODE: true,
    DD: true,
    DIV: true,
    DL: true,
    DT: true,
    EM: true,
    FIELDSET: true,
    FONT: true,
    H1: true,
    H2: true,
    H3: true,
    H4: true,
    H5: true,
    H6: true,
    HEADER: true,
    HR: true,
    I: true,
    INPUT: true,
    IMG: true,
    LABEL: true,
    LEGEND: true,
    LI: true,
    MAIN: true,
    OL: true,
    P: true,
    PRE: true,
    SECTION: true,
    SMALL: true,
    SOURCE: true,
    SPAN: true,
    STRONG: true,
    STYLE: true,
    SUB: true,
    SUP: true,
    TABLE: true,
    TBODY: true,
    TEMPLATE: true,
    TEXTAREA: true,
    TR: true,
    TD: true,
    TH: true,
    THEAD: true,
    UL: true,
    U: true,
    VIDEO: true,
    BUTTON: true
  };
  // Whitelist of tags to be replaced with DIV
  private readonly _contentTagWhiteList: { [key: string]: boolean } = { FORM: true, 'GOOGLE-SHEETS-HTML-ORIGIN': true };
  // Whitelist of allowed attributes
  private readonly _attributeWhitelist: { [key: string]: boolean } = {
    align: true,
    color: true,
    controls: true,
    height: true,
    href: true,
    id: true,
    rel: true, // Needed for rel="noopener noreferrer" on target="_blank" links
    src: true,
    style: true,
    target: true,
    title: true,
    type: true,
    width: true,
    class: true,
    alt: true, // Added 'alt'
    'aria-label': true, // Example of an ARIA attribute
    'data-custom': true, // Example of a data attribute
    'data-list': true, // Example of a data attribute
    'data-page-id': true // Id of the linked page, set by the page-link editor tool
  };
  // Whitelist of allowed CSS properties
  private readonly _cssWhitelist: { [key: string]: boolean } = {
    'background-color': true,
    color: true,
    'font-size': true,
    'font-weight': true,
    'text-align': true,
    'text-decoration': true,
    width: true
  };
  // Whitelist of allowed URI schemes per attribute
  private readonly _uriAttributeSchemas: { [key: string]: string[] } = {
    href: ['http:', 'https:', 'mailto:', 'ftp:', 'tel:'],
    action: ['http:', 'https:'],
    src: ['http:', 'https:', 'data:', 'm-files:', 'file:', 'ftp:', 'mailto:', 'pw:']
  };
  // Attributes that may contain URIs
  private readonly _uriAttributes: { [key: string]: boolean } = { href: true, action: true, src: true };

  // Methods
  /**
   * Checks whether the HTML contains a `<style>` tag, whose CSS applies to the whole page.
   * @param input The HTML string to check.
   * @returns True if a `<style>` tag is present, false otherwise.
   */
  public hasStyleTag(input: string | null | undefined): boolean {
    return /<style[\s>]/i.test(input ?? '');
  }

  /**
   * Returns a parsed HTML Document from the input string.
   * Uses DOMParser in the browser; falls back to document.implementation on SSR.
   */
  private parseHtmlDocument(input: string): Document {
    const DOMParserCtor = (this.document.defaultView as any)?.DOMParser;
    if (DOMParserCtor) {
      return new DOMParserCtor().parseFromString(input, 'text/html');
    }
    // SSR fallback: document.implementation is available via Angular's platform server
    const doc = this.document.implementation.createHTMLDocument('');
    doc.body.innerHTML = input;
    return doc;
  }

  /**
   * Sanitizes the input HTML string by removing disallowed tags, attributes, and styles.
   * @param input The HTML string to sanitize.
   * @param extraSelector Optional selector to allow additional elements.
   * @returns The sanitized HTML string.
   */
  public sanitizeHtml(input: string, extraSelector?: string): string {
    input = input.trim();
    if (input === '' || input === '<br>') {
      return '';
    }

    // Ensure the input is wrapped in a body tag
    if (!input.includes('<body')) {
      input = `<body>${input}</body>`;
    }

    const doc = this.parseHtmlDocument(input);

    if (doc.body.tagName !== 'BODY') {
      doc.body.remove();
    }
    if (typeof doc.createElement !== 'function') {
      (doc.createElement as any).remove();
    }

    const container = doc.createElement('DIV');

    // Sanitize each child of the body and append to the container
    for (const childNode of Array.from(doc.body.childNodes)) {
      const sanitizedChild = this.makeSanitizedCopy(childNode, doc, extraSelector);
      if (sanitizedChild) {
        container.appendChild(sanitizedChild);
      }
    }
    // Clean up the HTML output
    return container.innerHTML.replace(/<br[^>]*>(\S)/g, '<br>\n$1').replace(/div><div/g, 'div>\n<div');
  }

  /**
   * Creates a sanitized copy of a node, recursively processing its children.
   * @param node The node to sanitize.
   * @param doc The document context.
   * @param extraSelector Optional selector to allow additional elements.
   * @returns The sanitized node or null if disallowed.
   */
  private makeSanitizedCopy(node: Node, doc: Document, extraSelector?: string): HTMLElement | null {
    // Clone text nodes directly
    if (node.nodeType === Node.TEXT_NODE) {
      return node.cloneNode(true) as HTMLElement;
    }

    // Skip nodes that are not elements
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const element = node as HTMLElement;
    const nodeName = element.nodeName;
    const isTagNameAllowed = this._tagWhitelist[nodeName] || this._contentTagWhiteList[nodeName];
    const matchesExtraSelector = extraSelector && element.matches(extraSelector);

    // Skip elements that are not whitelisted
    if (!isTagNameAllowed && !matchesExtraSelector) {
      return null;
    }

    // Replace certain tags with DIV
    const newNodeName = this._contentTagWhiteList[nodeName] ? 'DIV' : nodeName;
    const newElement = doc.createElement(newNodeName);

    // Sanitize attributes
    this.sanitizeAttributes(element, newElement);

    // Recursively sanitize child nodes
    for (const childNode of Array.from(element.childNodes)) {
      const sanitizedChild = this.makeSanitizedCopy(childNode, doc, extraSelector);
      if (sanitizedChild) {
        newElement.appendChild(sanitizedChild);
      }
    }

    // Remove empty formatting tags
    if (['B', 'I', 'U', 'BUTTON'].includes(newElement.tagName) && newElement.innerHTML.trim() === '') {
      return null;
    }
    return newElement;
  }

  /**
   * Sanitizes the attributes of an element by copying only allowed attributes.
   * @param sourceElement The element with the original attributes.
   * @param targetElement The element to receive the sanitized attributes.
   */
  private sanitizeAttributes(sourceElement: HTMLElement, targetElement: HTMLElement): void {
    for (const attr of Array.from(sourceElement.attributes)) {
      // Skip disallowed attributes
      if (!this._attributeWhitelist[attr.name.toLowerCase()]) {
        continue;
      }

      // Handle style attributes separately
      if (attr.name.toLowerCase() === 'style') {
        this.sanitizeStyles(sourceElement, targetElement);
        continue;
      }

      // Validate URI attributes against allowed schemes
      if (this._uriAttributes[attr.name.toLowerCase()]) {
        const allowedSchemas = this._uriAttributeSchemas[attr.name.toLowerCase()];
        if (attr.value.includes(':') && !this.startsWithAny(attr.value, allowedSchemas)) {
          continue;
        }
      }

      // Copy the attribute to the target element
      targetElement.setAttribute(attr.name, attr.value);
    }
  }

  /**
   * Sanitizes inline styles by copying only allowed CSS properties.
   * @param sourceElement The element with the original styles.
   * @param targetElement The element to receive the sanitized styles.
   */
  private sanitizeStyles(sourceElement: HTMLElement, targetElement: HTMLElement): void {
    // Parse the raw style attribute instead of iterating CSSStyleDeclaration —
    // domino's (SSR) numeric index getter calls dasherizeProperty(index) which
    // throws "property.replace is not a function" when accessed via Array.from.
    const styleAttr = sourceElement.getAttribute('style');
    if (!styleAttr) return;
    for (const declaration of styleAttr.split(';')) {
      const colonIdx = declaration.indexOf(':');
      if (colonIdx === -1) continue;
      const property = declaration.slice(0, colonIdx).trim().toLowerCase();
      const value = declaration.slice(colonIdx + 1).trim();
      if (!property || !value) continue;
      if (this._cssWhitelist[property]) {
        targetElement.style.setProperty(property, value);
      }
    }
  }

  /**
   * Checks if a string starts with any of the provided substrings.
   * @param str The string to check.
   * @param substrings The substrings to look for.
   * @returns True if the string starts with any substring, false otherwise.
   */
  private startsWithAny(str: string, substrings: string[]): boolean {
    const lowerStr = str.toLowerCase();
    return substrings.some((substring) => lowerStr.startsWith(substring.toLowerCase()));
  }
}
